/**
 * 「字段应用至整单」弹层 —— 从 CooperationFormList/index.tsx 下沉为独立文件，
 * 供折叠模式（index.tsx）与表格模式（CooperationTableList.tsx）共用同一套交互
 * （「有内容时是否批量覆盖」coverMode + 「选择应用至整单的字段」coverKeys）。
 * 覆盖逻辑本身见 ./applyToWholeOrder.ts（纯函数，与本组件解耦）。
 */
import React, { useEffect, useMemo } from 'react';
import { Button, Popover, Space } from '@bedrock/components';
import { Copy2, ChevronDown } from '@bedrock/icons-react';
import { px2rem } from '@bedrock/utils';
import { ProForm, ProRadio, ProSelect } from '@bedrock/eed-react';
import { alignPropertyValues } from './CooperationPropertyForm';
import type { CoverMode } from './applyToWholeOrder';

export type ApplyToTheWholeOrderData = {
  // 覆盖模式：保留原值或者覆盖原值
  coverMode: CoverMode;
  // 覆盖字段
  coverKeys: string[];
};

export type DropdownListApplyToTheWholeOrderProps = {
  hideOptions?: {
    name: string;
    hide: boolean;
  }[];
  schema: any;
  onOk: (data: ApplyToTheWholeOrderData) => void;
  /** 触发按钮文案（默认"字段应用至整单"） */
  text?: string;
  /** 受控：是否打开 popover（由外层控制，如「更多」下拉菜单项触发） */
  open?: boolean;
  /** 受控：open 变化回调 */
  onOpenChange?: (open: boolean) => void;
  /** 隐藏默认触发按钮（仅受控 open 展示；锚点用不可见占位 span，供「更多」下拉菜单项打开，避免 Dropdown+Popover 嵌套冲突） */
  hideTrigger?: boolean;
};

// 下拉多选字段，应用到整单
export default function DropdownListApplyToTheWholeOrder(
  props: DropdownListApplyToTheWholeOrderProps,
) {
  const { schema, onOk, hideOptions = [], text = '字段应用至整单', open, onOpenChange, hideTrigger = false } = props;
  const [innerVisible, setInnerVisible] = React.useState(false);
  // 受控支持：外层传 open（如「更多」下拉菜单项）则用受控值，否则内部 state；hideTrigger 时锚点用不可见占位 span
  const visible = open ?? innerVisible;
  const setVisible = (v: boolean) => (onOpenChange ? onOpenChange(v) : setInnerVisible(v));
  const { viewBase } = React.useContext(px2rem);
  const [form] = ProForm.useForm();
  const widthStyle = {
    width: 280 * viewBase,
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      onOk?.(values);
      setVisible(false);
    });
  };
  const handleCancel = () => {
    setVisible(false);
  };

  const KeysOptions = useMemo(() => {
    const propertyValuesTemplate = alignPropertyValues([], schema);
    return [
      { name: '全选', value: '__SELECT_ALL__' },
      ...propertyValuesTemplate?.map?.((item: any) => {
        return {
          name: item.label,
          value: `property_values.${item.property_code}`,
        };
      }),
      { name: '考核指标', value: 'kpi_values' },
      { name: '必要权益', value: 'necessary_rights' },
      { name: '期望权益', value: 'expect_rights' },
      { name: '备注', value: 'remark' },
    ].filter((item) => !hideOptions.some((hideOption) => hideOption.name === item.value && hideOption.hide));
  }, [schema, hideOptions]);

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        coverMode: 'retain',
        coverKeys: KeysOptions.map((item) => item.value),
      });
    }
  }, [visible, KeysOptions]);

  const content = (
    /**
     * ⚠️ 这里**只能** `stopPropagation()`，**不得** `preventDefault()`。
     *
     * `stopPropagation` 是必要的：弹层内容与触发按钮同在一棵树上，不拦截冒泡会被 Popover
     * 当成「点击外部」而收起。
     *
     * 而 `preventDefault` 会连**默认行为**一起取消 —— radio/checkbox 的选中与随之而来的
     * `change` 事件，正是 click 的默认行为（实测：加 preventDefault 时点「覆盖原值」
     * `change` 触发 0 次，受控的 coverMode 永远停在 `retain`，「覆盖原值」在 UI 上不可达）。
     * 只拦截冒泡不影响默认行为，故删掉它即可。
     */
    <Space
      direction="vertical"
      size={8}
      style={widthStyle}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <ProForm
        form={form}
        submitter={false}
        initialValues={{
          coverMode: 'retain',
          coverKeys: ['__SELECT_ALL__'],
        }}
      >
        <ProRadio.Group
          name="coverMode"
          label="有内容时是否批量覆盖"
          fieldProps={{
            options: [
              { name: '保留原值', value: 'retain' },
              { name: '覆盖原值', value: 'overwrite' },
            ],
          }}
        />
        <ProSelect
          required
          multiple
          name="coverKeys"
          label="选择应用至整单的字段"
          fieldProps={{
            multiple: true,
            options: KeysOptions,
            showSelectAllOption: true,
            allDisplay: 'options',
            returnOnlyLeaf: true,
            type: 'label',
            maxTagCount: 'responsive',
            showConfirm: true,
            showReset: true,
          }}
        />
      </ProForm>
      <Space className="justify-end" style={widthStyle}>
        <Button onClick={handleCancel} size="small">取消</Button>
        <Button onClick={handleOk} type="primary" size="small">
          确定
        </Button>
      </Space>
    </Space>
  );
  const triggerEl = hideTrigger ? (
    <span style={{ visibility: 'hidden', display: 'inline-block', width: 0, height: 0 }} />
  ) : (
    <Button
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        setVisible(true);
      }}
      icon={<Copy2 />}
      type="text-subtle"
      size="xSmall"
      suffixIcon={<ChevronDown />}
    >
      {text}
    </Button>
  );
  return (
    <Popover
      content={content}
      trigger="click"
      visible={visible}
      onVisibleChange={setVisible}
      showArrow={false}
    >
      {triggerEl}
    </Popover>
  );
}
